import { TemplateElement, html, defineElement, when, classMap, unsafeHTML } from '@webtides/element-js';
import style from './step-cards.css';
import { responsiveImage } from '../../../../directives/images.js';
import '../../../button/button.js';

/**
 * @typedef {Object} StepCardProperties
 * @property {ImageProperties} [image]
 * @property {string} [headline]
 * @property {string} [text]
 */

/**
 * Numbered process steps, rendered as a row of cards connected by a dashed line.
 *
 * @property {string} headline - Headline above the steps
 * @property {StepCardProperties[]} cards - The steps, numbered in order
 * @property {LinkProperties} link - Optional call to action below the steps
 */
export default class StepCards extends TemplateElement {
    constructor() {
        super({ styles: [style] });
    }

    properties() {
        return {
            headline: '',
            cards: [],
            link: undefined,
        };
    }

    template() {
        const gridClasses = {
            '@sm:grid-cols-2': this.cards.length >= 2,
            '@[768px]:grid-cols-3': this.cards.length === 3,
            '@[960px]:grid-cols-4': this.cards.length === 4,
        };
        const connectorClasses = {
            hidden: true,
            '@sm:block': this.cards.length === 2,
            '@[768px]:block': this.cards.length === 3,
            '@[960px]:block': this.cards.length === 4,
        };

        return html`
            <div class="flex flex-col gap-32">
                ${when(
                    this.headline,
                    html`
                        <h2
                            class="flex-headline2-medium flex-scale-small text-green-medium text-center text-balance mb-0"
                        >
                            ${this.headline}
                        </h2>
                    `,
                )}
                <ol class="grid gap-24 list-none p-0 m-0 ${classMap(gridClasses)}">
                    ${this.cards.map((card, index) => this._renderCard(card, index, connectorClasses))}
                </ol>
                ${when(
                    this.link?.url && this.link?.text,
                    html`
                        <mb-button
                            class="self-center"
                            href="${this.link?.url}"
                            variant="secondary"
                            element="step cards"
                            tracking-label="${this.link?.trackingLabel || ''}"
                        >
                            ${this.link?.text}
                        </mb-button>
                    `,
                )}
            </div>
        `;
    }

    _renderCard(card, index, connectorClasses) {
        return html`
            <li class="relative flex flex-col items-center gap-24 p-16 rounded-[16px] bg-white">
                ${when(index > 0, html`<span class="step-connector text-gray-50 ${classMap(connectorClasses)}"></span>`)}
                <span
                    class="flex items-center justify-center shrink-0 size-[45px] rounded-full bg-green-medium text-white fix-title3-medium"
                >
                    ${String(index + 1).padStart(2, '0')}
                </span>
                <div class="flex flex-col items-center gap-16 w-full">
                    ${when(card.image?.srcset, responsiveImage(card.image, 'size-[136px] object-contain', false))}
                    ${when(
                        card.headline,
                        html`<p class="fix-body1-medium text-green-medium text-center w-full mb-0">${card.headline}</p>`,
                    )}
                    ${when(
                        card.text,
                        html`<div class="step-text fix-body2 text-green-light w-full">${unsafeHTML(card.text || '')}</div>`,
                    )}
                </div>
            </li>
        `;
    }
}

defineElement('mb-step-cards', StepCards);
